// Formular-Logik des Ansprechpartner-Profils — bewusst OHNE React/Griffel,
// damit sie in node getestet werden kann (wie `table/filterModel.ts`).

import {
  PP_FUNKTIONSBEREICHE_MIT_SPARTEN,
  PP_FUNKTIONSBEREICH_MIT_ANGABE,
  PP_POSITION_MIT_ANGABE,
  PP_POSITION_MIT_VON,
  PP_SPARTE_SONSTIGES,
  PP_SPARTE_SPEZIALKONZEPTE,
} from "../../data/produktpartnerKatalog";
import type { PartnerProfile } from "../../services/produktpartnerService";

/** Feldnamen im Formular-State — geteilt zwischen Anlage und Detailseite. */
export const PP_FIELDS = {
  partnerContactId: "ppPartnerContactId",
  standort: "ppStandort",
  organisationseinheit: "ppOrganisationseinheit",
  position: "ppPosition",
  positionVon: "ppPositionVon",
  positionSonstiges: "ppPositionSonstiges",
  positionFreitext: "ppPositionFreitext",
  funktionsbereich: "ppFunktionsbereich",
  funktionsbereichSonstiges: "ppFunktionsbereichSonstiges",
  sparten: "ppSparten",
  spartenSpezialkonzepte: "ppSpartenSpezialkonzepte",
  spartenSonstiges: "ppSpartenSonstiges",
  eintrittsdatum: "ppEintrittsdatum",
  hobbys: "ppHobbys",
  vorlieben: "ppVorlieben",
  bemerkungen: "ppBemerkungen",
} as const;

/** Liest die Sparten-Mehrfachauswahl aus dem serialisierten State. */
export const parseSparten = (raw: string): string[] => {
  if (!raw.trim()) return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
};

/**
 * Baut aus dem Formular-State den Rumpf für `PUT /contact-partner-profile/:id`.
 *
 * Leere Strings werden zu `null`, damit ein geleertes Feld beim Speichern auch
 * wirklich leer wird statt als "" stehen zu bleiben. Freitexte, deren Auslöser
 * nicht (mehr) gewählt ist, fallen weg — sonst bliebe die „Assistenz von"-Angabe
 * an einer inzwischen zur Teamleitung gewordenen Person kleben. Verborgene
 * Sparten und ihre Zusatztexte bleiben dagegen als Bestandsangaben erhalten.
 *
 * Rein und ohne Seiteneffekte, damit unit-testbar.
 */
export function collectPartnerProfile(
  f: (key: string) => string,
): Omit<PartnerProfile, "contactId"> {
  const t = (key: string): string | null => {
    const v = f(key).trim();
    return v ? v : null;
  };

  const position = t(PP_FIELDS.position);
  const funktionsbereich = t(PP_FIELDS.funktionsbereich);
  const sparten = parseSparten(f(PP_FIELDS.sparten));
  const spartenAktiv =
    !!funktionsbereich && PP_FUNKTIONSBEREICHE_MIT_SPARTEN.has(funktionsbereich);
  const partnerRaw = Number(f(PP_FIELDS.partnerContactId));

  return {
    partnerContactId: Number.isFinite(partnerRaw) && partnerRaw > 0 ? partnerRaw : null,
    standort: t(PP_FIELDS.standort),
    organisationseinheit: t(PP_FIELDS.organisationseinheit),
    position,
    positionVon: position === PP_POSITION_MIT_VON ? t(PP_FIELDS.positionVon) : null,
    positionSonstiges:
      position === PP_POSITION_MIT_ANGABE ? t(PP_FIELDS.positionSonstiges) : null,
    positionFreitext: t(PP_FIELDS.positionFreitext),
    funktionsbereich,
    funktionsbereichSonstiges:
      funktionsbereich === PP_FUNKTIONSBEREICH_MIT_ANGABE
        ? t(PP_FIELDS.funktionsbereichSonstiges)
        : null,
    sparten: sparten.length > 0 ? sparten : null,
    spartenSpezialkonzepte:
      (!spartenAktiv || sparten.includes(PP_SPARTE_SPEZIALKONZEPTE))
        ? t(PP_FIELDS.spartenSpezialkonzepte)
        : null,
    spartenSonstiges:
      (!spartenAktiv || sparten.includes(PP_SPARTE_SONSTIGES))
        ? t(PP_FIELDS.spartenSonstiges)
        : null,
    eintrittsdatum: t(PP_FIELDS.eintrittsdatum),
    hobbys: t(PP_FIELDS.hobbys),
    vorlieben: t(PP_FIELDS.vorlieben),
    bemerkungen: t(PP_FIELDS.bemerkungen),
  };
}

/**
 * Gegenrichtung: ein geladenes Profil zurück in den Formular-State. Wird von der
 * Detailseite gebraucht, um die Felder vorzubelegen.
 */
export function partnerProfileToFormState(
  profile: PartnerProfile | null,
): Record<string, string> {
  const s = (v: unknown): string => (v == null ? "" : String(v));
  return {
    [PP_FIELDS.partnerContactId]: profile?.partnerContactId
      ? String(profile.partnerContactId)
      : "",
    [PP_FIELDS.standort]: s(profile?.standort),
    [PP_FIELDS.organisationseinheit]: s(profile?.organisationseinheit),
    [PP_FIELDS.position]: s(profile?.position),
    [PP_FIELDS.positionVon]: s(profile?.positionVon),
    [PP_FIELDS.positionSonstiges]: s(profile?.positionSonstiges),
    [PP_FIELDS.positionFreitext]: s(profile?.positionFreitext),
    [PP_FIELDS.funktionsbereich]: s(profile?.funktionsbereich),
    [PP_FIELDS.funktionsbereichSonstiges]: s(profile?.funktionsbereichSonstiges),
    [PP_FIELDS.sparten]: JSON.stringify(profile?.sparten ?? []),
    [PP_FIELDS.spartenSpezialkonzepte]: s(profile?.spartenSpezialkonzepte),
    [PP_FIELDS.spartenSonstiges]: s(profile?.spartenSonstiges),
    // `date`-Spalten kommen als "2026-07-26" oder als ISO-Zeitstempel zurück;
    // das <input type="date"> versteht nur den reinen Datumsteil.
    [PP_FIELDS.eintrittsdatum]: s(profile?.eintrittsdatum).slice(0, 10),
    [PP_FIELDS.hobbys]: s(profile?.hobbys),
    [PP_FIELDS.vorlieben]: s(profile?.vorlieben),
    [PP_FIELDS.bemerkungen]: s(profile?.bemerkungen),
  };
}

export function validatePartnerProfile(f: (key: string) => string): string[] {
  const errors: string[] = [];
  const id = Number(f(PP_FIELDS.partnerContactId));
  if (!Number.isInteger(id) || id <= 0) errors.push("Gesellschaft ist erforderlich.");
  if (!f(PP_FIELDS.position).trim()) errors.push("Position ist erforderlich.");
  return errors;
}
